import type { Command } from './commands.js';
import { TICK_MS } from './constants.js';
import type { LevelDef } from './level.js';
import { applyAttrition } from './rules/attrition.js';
import { carAt, onCar } from './rules/cars.js';
import {
  addStop,
  advanceElevator,
  clearStops,
  markFirst,
  queueBoarding,
  requestClose,
  routeTo,
} from './rules/elevator.js';
import { evaluateObjective } from './rules/objective.js';
import { chargePowers, usePower } from './rules/powers.js';
import { applySpawns } from './rules/spawning.js';
import type { GameState } from './types.js';

/**
 * Advance the simulation by exactly one tick.
 *
 * MUTATES `state` in place and returns nothing. This is deliberate: a difficulty
 * sweep runs tens of millions of ticks, and allocating a fresh state per tick
 * would dominate the runtime. Snapshot with `cloneState` when you need one.
 *
 * The order below is the deterministic contract. Changing it changes every
 * recorded replay, so treat it as a breaking change to the replay format.
 */
export function step(state: GameState, level: LevelDef, commands: readonly Command[] = []): void {
  if (state.status !== 'playing') return;

  applyCommands(state, commands);

  state.tick += 1;
  state.elapsedMs += TICK_MS;

  applySpawns(state);
  applyAttrition(state);
  advanceElevator(state);
  // The rest of the bank (ADR-0034), in order, by the same rules. Empty on
  // every level with one car, so nothing about those runs moves.
  for (const car of state.extraCars) onCar(state, car, () => advanceElevator(state));
  chargePowers(state);
  evaluateObjective(state, level.objective);
}

function applyCommands(state: GameState, commands: readonly Command[]): void {
  for (const command of commands) {
    // A command names its car (ADR-0034); one that names none is for the first.
    const car = carAt(state, 'car' in command ? (command.car ?? 0) : 0);
    if (car) onCar(state, car, () => applyCommand(state, command));
  }
}

function applyCommand(state: GameState, command: Command): void {
  switch (command.type) {
    case 'callTo':
      routeTo(state, command.floor);
      break;
    case 'addStop':
      addStop(state, command.floor);
      break;
    case 'clearStops':
      clearStops(state);
      break;
    case 'usePower':
      usePower(state, command.power, state.elevator, command.passengerId);
      break;
    case 'board':
      queueBoarding(state, command.passengerId);
      break;
    case 'closeDoors':
      requestClose(state);
      break;
    case 'prioritise':
      markFirst(state, command.passengerId);
      break;
  }
}

/** Run until the level ends or `maxTicks` is reached. Used by bots and replay verification. */
export function runUntilEnd(
  state: GameState,
  level: LevelDef,
  produceCommands: (state: GameState) => readonly Command[],
  maxTicks = 60_000,
): void {
  while (state.status === 'playing' && state.tick < maxTicks) {
    step(state, level, produceCommands(state));
  }
}
